// Ask thread model + persistence (investigating-analyst Task 12).
//
// Pure, dependency-free, never throws. Client side of the server contract in
// proxy-server/conformance/askDelivery.js `buildThreadHistory`:
//   - the request carries questions + direct answers + evidence refs only,
//     never the markdown, the trail or any tool output;
//   - same bounds (THREAD_MAX_TURNS / THREAD_MAX_BYTES) and per-field caps.
// The server masks ID-class values (maskIdText / safeValue). This module masks
// nothing, and it never persists raw tool output: trail entries arrive already
// masked and capped by the server (`trail[].summary`), and saveThread whitelists
// fields so anything else on a turn (markdown, toolTrace, rows) is dropped.
//
// Storage: localStorage, one key per session (threadKey). The key prefix is
// this feature's own; it deliberately does not share MainApp's 'groupedResults.slim'
// (two-tier history) nor the legacy sessionStorage 'checks.askThread' that
// AgentPanel used before Task 15 (askFlow.loadInitialThread migrates it). The history_snapshot guard stays in
// TrafficContext / MainApp; nothing here reacts to a snapshot, and the test
// asserts neither file references this module.

export const THREAD_MAX_TURNS = 6;
export const THREAD_MAX_BYTES = 8192;
export const THREAD_STORE_MAX_TURNS = 50;

export const TURN_STATUSES = ['working', 'done', 'error', 'capHit', 'degraded'];
const CONTEXT_STATUSES = ['done', 'capHit', 'degraded'];

// Server per-field caps (askDelivery.js), mirrored.
const CTX_Q_MAX = 500;
const CTX_A_MAX = 1200;
const CTX_EVIDENCE_MAX = 6;
const CTX_EVIDENCE_NAME_MAX = 120;
const CTX_EVIDENCE_VALUE_MAX = 200;

// Persisted (display) caps. Looser than the context caps, still bounded.
const STORE_Q_MAX = 2000;
const STORE_A_MAX = 2000;
const STORE_EVIDENCE_MAX = 20;
const STORE_NEXT_MAX = 6;
const STORE_NEXT_LEN = 300;
const STORE_FOLLOWUP_MAX = 6;
const STORE_FOLLOWUP_LEN = 200;
const STORE_TRAIL_MAX = 60;
const STORE_TRAIL_SUMMARY_MAX = 160;
const STORE_ERROR_MAX = 300;
const USAGE_KEYS = ['inputTokens', 'outputTokens', 'totalTokens', 'cap', 'capHit'];

const KEY_PREFIX = 'checks.askThread.v2:';

// Same conversion as the dashboard's ts() (InspectraNetworkView.jsx `_toMs`):
// the proxy sends an ISO string, MainApp overrides live events with a number.
// That helper lives in a .jsx view, and importing a view into a util pulls
// react-router into Jest (frontend/CLAUDE.md), so the 4-line conversion is
// repeated here rather than imported.
export const toMs = (v) => {
  if (!v && v !== 0) return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const n = Date.parse(v);
  return Number.isNaN(n) ? 0 : n;
};

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const str = (v, max) => (typeof v === 'string' ? v.slice(0, max) : '');

function newId(prefix) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function byteLen(s) {
  let n = 0;
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff) { n += 4; i += 1; } else n += 3;
  }
  return n;
}

// threadKey(sessionId): the dashboard session id is `debug_ssid` in
// TrafficContext; callers pass it. No session id => one shared 'default' slot.
export function threadKey(sessionId) {
  const id = typeof sessionId === 'string' && sessionId ? sessionId : 'default';
  return KEY_PREFIX + id;
}

export function newThread() {
  return { threadId: newId('thr'), turns: [] };
}

function safeThread(thread) {
  if (!isObj(thread)) return newThread();
  return {
    threadId: typeof thread.threadId === 'string' && thread.threadId ? thread.threadId : newId('thr'),
    turns: Array.isArray(thread.turns) ? thread.turns.filter(isObj) : [],
  };
}

function trimStore(turns) {
  return turns.length > THREAD_STORE_MAX_TURNS ? turns.slice(turns.length - THREAD_STORE_MAX_TURNS) : turns;
}

export function appendTurn(thread, question) {
  const base = safeThread(thread);
  const turn = {
    id: newId('turn'),
    question: str(question, STORE_Q_MAX),
    status: 'working',
    trail: [],
    startedAt: Date.now(),
  };
  return { thread: { ...base, turns: trimStore([...base.turns, turn]) }, turnId: turn.id };
}

// In-memory patch whitelist. `markdown`, `extra` (the pre-investigation rungs' table
// fields) and `rowIds` (requestIds of the rows posted with the question, for the evidence
// jump) are allowed in memory and are dropped on save (slimTurn whitelists).
const PATCH_FIELDS = [
  'answer', 'jobId', 'trail', 'usage', 'evidence', 'next', 'followUps',
  'markdown', 'error', 'partial', 'degraded', 'scope', 'completedAt', 'extra', 'rowIds',
];

export function updateTurn(thread, turnId, patch) {
  const base = safeThread(thread);
  if (!isObj(patch)) return base;
  const idx = base.turns.findIndex((t) => t.id === turnId);
  if (idx < 0) return base;
  const next = { ...base.turns[idx] };
  if (TURN_STATUSES.includes(patch.status)) next.status = patch.status;
  for (const f of PATCH_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(patch, f) && patch[f] !== undefined) next[f] = patch[f];
  }
  const turns = base.turns.slice();
  turns[idx] = next;
  return { ...base, turns };
}

// `keepRequestId` is true only for storage: the persisted evidence keeps the jump id, the context sent with the next
// question does not (the server never reads it, and it would count against the 8 KB context bound).
function cleanEvidence(list, max, valueMax, keepRequestId = false) {
  const out = [];
  if (!Array.isArray(list)) return out;
  for (const e of list) {
    if (out.length >= max) break;
    if (!isObj(e)) continue;
    const endpoint = str(e.endpoint, CTX_EVIDENCE_NAME_MAX);
    const field = str(e.field, CTX_EVIDENCE_NAME_MAX);
    if (!endpoint && !field) continue;
    const v = e.value;
    const value = (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')
      ? String(v).slice(0, valueMax) : '';
    const item = { endpoint, field, value };
    if (typeof e.rowRef === 'string' || (typeof e.rowRef === 'number' && Number.isFinite(e.rowRef))) item.rowRef = e.rowRef;
    if (keepRequestId && typeof e.requestId === 'string' && e.requestId.trim()) item.requestId = e.requestId.slice(0, 120);
    out.push(item);
  }
  return out;
}

/**
 * buildThreadContext(thread) -> [{ question, answer, evidence }]
 * The `thread` request field. Finished turns only (done|capHit|degraded) that
 * have a direct answer; newest THREAD_MAX_TURNS; whole oldest turns dropped
 * until the JSON is within THREAD_MAX_BYTES (UTF-8 bytes).
 */
export function buildThreadContext(thread) {
  if (!isObj(thread) || !Array.isArray(thread.turns)) return [];
  const ctx = [];
  for (const t of thread.turns) {
    if (!isObj(t) || !CONTEXT_STATUSES.includes(t.status)) continue;
    const answer = str(t.answer, CTX_A_MAX);
    const question = str(t.question, CTX_Q_MAX);
    if (!answer || !question) continue;
    ctx.push({ question, answer, evidence: cleanEvidence(t.evidence, CTX_EVIDENCE_MAX, CTX_EVIDENCE_VALUE_MAX) });
  }
  let kept = ctx.slice(-THREAD_MAX_TURNS);
  while (kept.length && byteLen(JSON.stringify(kept)) > THREAD_MAX_BYTES) kept = kept.slice(1);
  return kept;
}

function cleanTrail(trail) {
  const out = [];
  if (!Array.isArray(trail)) return out;
  for (const e of trail) {
    if (out.length >= STORE_TRAIL_MAX) break;
    if (!isObj(e) || typeof e.tool !== 'string') continue;
    const item = { tool: e.tool.slice(0, 60), summary: str(e.summary, STORE_TRAIL_SUMMARY_MAX) };
    if (typeof e.ts === 'string' || (typeof e.ts === 'number' && Number.isFinite(e.ts))) item.ts = e.ts;
    out.push(item);
  }
  return out;
}

function cleanStrings(list, max, len) {
  if (!Array.isArray(list)) return [];
  return list.filter((s) => typeof s === 'string' && s).slice(0, max).map((s) => s.slice(0, len));
}

function cleanUsage(u) {
  if (!isObj(u)) return undefined;
  const out = {};
  for (const k of USAGE_KEYS) {
    if (typeof u[k] === 'number' && Number.isFinite(u[k])) out[k] = u[k];
    else if (typeof u[k] === 'boolean') out[k] = u[k];
  }
  return Object.keys(out).length ? out : undefined;
}

// Whitelist copy of one turn: the only fields that reach storage.
function slimTurn(t) {
  const out = {
    id: typeof t.id === 'string' && t.id ? t.id : newId('turn'),
    question: str(t.question, STORE_Q_MAX),
    status: TURN_STATUSES.includes(t.status) ? t.status : 'error',
    trail: cleanTrail(t.trail),
  };
  if (typeof t.answer === 'string') out.answer = t.answer.slice(0, STORE_A_MAX);
  if (typeof t.jobId === 'string' && t.jobId) out.jobId = t.jobId.slice(0, 120);
  const evidence = cleanEvidence(t.evidence, STORE_EVIDENCE_MAX, CTX_EVIDENCE_VALUE_MAX, true);
  if (evidence.length) out.evidence = evidence;
  const next = cleanStrings(t.next, STORE_NEXT_MAX, STORE_NEXT_LEN);
  if (next.length) out.next = next;
  const followUps = cleanStrings(t.followUps, STORE_FOLLOWUP_MAX, STORE_FOLLOWUP_LEN);
  if (followUps.length) out.followUps = followUps;
  const usage = cleanUsage(t.usage);
  if (usage) out.usage = usage;
  if (typeof t.error === 'string' && t.error) out.error = t.error.slice(0, STORE_ERROR_MAX);
  if (t.partial === true) out.partial = true;
  if (t.degraded === true) out.degraded = true;
  if (typeof t.startedAt === 'string' || (typeof t.startedAt === 'number' && Number.isFinite(t.startedAt))) out.startedAt = t.startedAt;
  return out;
}

export function saveThread(storage, key, thread) {
  try {
    if (!storage || typeof storage.setItem !== 'function') return false;
    const base = safeThread(thread);
    const turns = trimStore(base.turns.map(slimTurn));
    storage.setItem(key, JSON.stringify({ threadId: base.threadId, turns }));
    return true;
  } catch {
    return false; // quota / private mode: the thread just won't survive a reload
  }
}

// One stored turn -> a live turn, or null when it is unusable. Also reads the
// legacy AgentPanel shape `{ q, out: { answer } }` as a finished turn.
function restoreTurn(raw) {
  if (!isObj(raw)) return null;
  const legacy = typeof raw.question !== 'string' && typeof raw.q === 'string';
  const question = legacy ? raw.q : raw.question;
  if (typeof question !== 'string' || !question.trim()) return null;
  if (legacy) {
    const answer = isObj(raw.out) && typeof raw.out.answer === 'string' ? raw.out.answer : '';
    return { id: newId('turn'), question: question.slice(0, STORE_Q_MAX), status: 'done', answer: answer.slice(0, STORE_A_MAX), trail: [] };
  }
  const t = slimTurn(raw);
  // A working turn with no job to resume was awaited by a page that is gone.
  if (t.status === 'working' && !t.jobId) {
    t.status = 'error';
    if (!t.error) t.error = 'Interrupted by a reload before the answer arrived.';
  }
  return t;
}

export function loadThread(storage, key) {
  try {
    if (!storage || typeof storage.getItem !== 'function') return newThread();
    const raw = storage.getItem(key);
    if (!raw) return newThread();
    const parsed = JSON.parse(raw);
    let list;
    let threadId;
    if (Array.isArray(parsed)) list = parsed;
    else if (isObj(parsed) && Array.isArray(parsed.turns)) { list = parsed.turns; threadId = parsed.threadId; } else return newThread();
    const turns = trimStore(list.map(restoreTurn).filter(Boolean));
    return { threadId: typeof threadId === 'string' && threadId ? threadId : newId('thr'), turns };
  } catch {
    return newThread();
  }
}

// Explicit user action only (New session / a "clear conversation" control).
// Never wire this to a history_snapshot, empty or not (AC 88).
export function clearThread(storage, key) {
  try {
    if (storage && typeof storage.removeItem === 'function') storage.removeItem(key);
  } catch { /* nothing to clear */ }
}
